import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { prov, provTap } from "@/lib/provenance";
import { useFlash } from "@/floor/useFlash";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { ShelfCleanupDays } from "@/components/ShelfCleanupDays";
import { fullLabel } from "@/components/WhereItIs";
import { CheckCircle2, Loader2, Undo2 } from "lucide-react";
import { ContainerRow, LotCrateRow } from "@/floor/ShelfCleanupRows";

// SHELF CLEAN-UP (his call, 2026-10-21): containers on no shelf, biggest first. A row ticks off
// only when the container is really on a shelf — the same scan-the-shelf move, with its undo.
export default function FloorShelfCleanup() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [shelving, setShelving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);
  const { trigger, FlashOverlay } = useFlash();

  const load = () => api.get("/shelf-cleanup").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const shelve = async (code, meta) => {
    const row = shelving;
    setBusy(true);
    try {
      const { data: r } = await api.post("/containers/move", { container_scan: row.id, location_scan: code,
                                                               provenance: prov("shelf_cleanup", meta) });
      if (!r.ok) { trigger("error", r.message || "NOT SHELVED"); return; }
      trigger("success", `SHELVED · ${r.destination?.short_code || code}`);
      setDone((d) => [{ ...row, dest: fullLabel(r.destination) || code }, ...d]);
      setShelving(null);
      load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "NOT SHELVED"); }
    finally { setBusy(false); }
  };

  const undo = async (row) => {
    try {
      const { data: r } = await api.post("/move-item/undo", { kind: "container", item_id: row.id,
                                                              previous_location_id: null, provenance: provTap("shelf_cleanup") });
      if (r.ok === false) { trigger("error", r.message || "UNDO FAILED"); return; }
      trigger("success", "BACK ON THE LIST");
      setDone((d) => d.filter((x) => x.id !== row.id));
      load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "UNDO FAILED"); }
  };

  const mark = async (row, found) => {
    try {
      await api.post(`/shelf-cleanup/${row.id}/${found ? "found" : "cant-find"}`);
      load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "FAILED"); }
  };

  if (data === null) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;
  if (data === false) return (
    <div className="flex-1 p-6 flex flex-col items-start gap-4" data-testid="shelf-cleanup-error">
      <div className="text-xl text-slate-600">Couldn't load the list.</div>
      <button type="button" data-testid="shelf-cleanup-retry" onClick={() => { setData(null); load(); }}
        className="h-14 px-6 rounded-xl bg-[#0F172A] text-white text-lg font-black">Try again</button>
    </div>
  );
  const n = data.now;

  return (
    <div className="flex-1 flex flex-col p-4 sm:p-6 gap-4 overflow-y-auto" data-testid="shelf-cleanup">
      {FlashOverlay}
      <div>
        <div className="text-3xl font-black tracking-tight">Shelf clean-up</div>
        <div className="text-base text-slate-600 mt-1" data-testid="shelf-cleanup-count">
          <b>{n.containers_with_parts.toLocaleString()}</b> container{n.containers_with_parts === 1 ? "" : "s"} with parts on no shelf
          {" · "}<b>{n.lot_crates_with_parts.toLocaleString()}</b> lot{n.lot_crates_with_parts === 1 ? "" : "s"} with parts not put away
          {" · "}{(n.empty_containers + n.empty_lot_crates).toLocaleString()} empty (counted, not listed)
        </div>
        <div className="mt-3"><ShelfCleanupDays days={data.days} testid="shelf-cleanup-days" /></div>
      </div>

      {done.length > 0 && (
        <div className="space-y-2" data-testid="shelf-cleanup-done">
          {done.map((d) => (
            <div key={d.id} data-testid={`shelf-done-${d.id}`} className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-lg font-black font-mono truncate">{d.code}</div>
                <div className="text-sm text-emerald-800 truncate">Shelved · {d.dest}</div>
              </div>
              <button type="button" data-testid={`shelf-undo-${d.id}`} onClick={() => undo(d)}
                className="h-12 px-3 rounded-xl border-2 border-emerald-400 font-bold flex items-center gap-1 shrink-0"><Undo2 className="w-5 h-5" />Undo</button>
            </div>
          ))}
        </div>
      )}

      <section data-testid="shelf-cleanup-containers">
        <div className="text-sm font-black uppercase tracking-[0.15em] text-[#B01E28]">Containers · biggest first</div>
        {data.containers.length === 0
          ? <div className="py-6 text-lg font-bold text-emerald-700" data-testid="shelf-cleanup-containers-clear">Every container with parts is on a shelf.</div>
          : <div className="space-y-3 mt-2">{data.containers.map((c) => (
              <ContainerRow key={c.id} c={c} onShelve={() => setShelving(c)} onMark={(found) => mark(c, found)} />))}</div>}
      </section>

      <section data-testid="shelf-cleanup-lots">
        <div className="text-sm font-black uppercase tracking-[0.15em] text-slate-700">Lots · parts to put away · biggest first</div>
        <p className="text-sm text-slate-600 mt-1" data-testid="shelf-cleanup-lots-why">
          A lot crate isn't a crate on the floor. It's the record the app keeps for each lot to track its parts, not
          something you print a label for, so there's nothing to shelve. The job is to put each of its parts away:
          open the lot, find the parts by their tags, and put each one on a shelf. A lot drops off this list when its
          last part is put away.
        </p>
        {data.lot_crates.length === 0
          ? <div className="py-6 text-lg font-bold text-emerald-700" data-testid="shelf-cleanup-lots-clear">No lot has parts waiting.</div>
          : <div className="space-y-3 mt-2">{data.lot_crates.map((l) => (
              <LotCrateRow key={l.id} l={l} onOpen={() => navigate(`/floor/location/${l.id}`)} />))}</div>}
      </section>

      <FloorScanOverlay open={!!shelving} testid="shelf-scan-overlay"
        title={shelving ? `Shelve ${shelving.code}` : ""} sub="Scan the shelf it's going on"
        submitLabel="Shelve it" busy={busy} onScan={shelve} onClose={() => setShelving(null)} />
    </div>
  );
}
